import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { journalSourceLabel } from '@accounting/shared';
import { useJournalEntries, useDeleteJournalEntry } from '../../hooks/useJournalEntries';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { buildAccountOptions } from '../../lib/accountOptions';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

const HIDE_VOID_KEY = 'accounting:journal:hideVoid';
const PAGE_SIZES = [50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

export const Route = createFileRoute('/_auth/journal/')({
  // Account filter + paging live in the URL so a filtered view survives a
  // reload and can be pasted to someone else. `account` is named to match the
  // General Ledger route, so links between the two carry over.
  validateSearch: (
    s: Record<string, unknown>,
  ): { account?: string; page?: number; pageSize?: number } => {
    const page = Number(s.page);
    const pageSize = Number(s.pageSize);
    return {
      account: typeof s.account === 'string' && s.account ? s.account : undefined,
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : undefined,
      pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : undefined,
    };
  },
  component: JournalPage,
});

function JournalPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const accountId = search.account ?? '';
  const page = (search.page ?? 1) - 1; // 0-based internally
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE;

  const [hideVoid, setHideVoid] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(HIDE_VOID_KEY) === 'true';
  });

  // Every control funnels through here. Changing a *filter* (not the page
  // itself) resets to page 1 — staying on page 7 of a result set that just
  // shrank to 2 pages would show an empty table and a pager that lies.
  const setSearch = (next: { account?: string; page?: number; pageSize?: number }) =>
    void navigate({
      search: (prev) => {
        const merged = { ...prev, ...next };
        if (next.account !== undefined || next.pageSize !== undefined) merged.page = 1;
        // Defaults are omitted from the URL to keep it readable.
        if (!merged.account) delete merged.account;
        if (merged.page === 1) delete merged.page;
        if (merged.pageSize === DEFAULT_PAGE_SIZE) delete merged.pageSize;
        return merged;
      },
    });

  // Account ids are company-scoped, so a filter carried across a company switch
  // matches nothing and reads as "this company has no entries". Clear it on the
  // switch itself rather than inferring staleness from the loaded account list —
  // that would also fire during the load, when the list is legitimately empty.
  const lastCompanyId = useRef(companyId);
  useEffect(() => {
    if (lastCompanyId.current === companyId) return;
    lastCompanyId.current = companyId;
    if (search.account) {
      // `replace` so Back doesn't bounce between the cleared and stale filter.
      void navigate({
        replace: true,
        search: (prev) => {
          const next = { ...prev };
          delete next.account;
          delete next.page;
          return next;
        },
      });
    }
  }, [companyId, search.account, navigate]);

  const { data: accounts = [] } = useAccounts(companyId);
  const accountLabel = useMemo(() => {
    if (!accountId) return null;
    const opt = buildAccountOptions(accounts).find((o) => o.id === accountId);
    return opt?.fullPath ?? null;
  }, [accounts, accountId]);

  const { data, isLoading, error, isFetching } = useJournalEntries(companyId, {
    includeVoid: !hideVoid,
    limit: pageSize,
    offset: page * pageSize,
    accountId: accountId || undefined,
  });
  const entries = data?.entries ?? [];
  // Server-side count of everything matching the current filters (account +
  // hide-voided), not just this page — so the pager and the caption agree.
  const total = data?.total ?? 0;
  const voidCount = entries.filter((e) => e.isVoid).length;

  const del = useDeleteJournalEntry(companyId);
  // Escalating confirms, matching the General Ledger's delete: one step for a
  // plain entry, a pointed warning when a bank transaction would be released,
  // and two for a reconciled one. Deleting is permanent — voiding keeps the
  // entry visible — so the reconciled path is deliberately hard to walk
  // through by reflex.
  const onDelete = (e: (typeof entries)[number]) => {
    if (e.isLocked) {
      if (
        !confirm(
          `WARNING: ${e.entryNumber} is RECONCILED. Deleting it leaves a gap in the reconciled ledger and the linked reconciliation will no longer reference a valid entry.\n\nContinue?`,
        )
      )
        return;
      if (!confirm(`Final confirmation — permanently delete ${e.entryNumber}? This cannot be undone.`))
        return;
    } else if (e.bankMatchStatus === 'matched' || e.bankMatchStatus === 'reconciled') {
      if (
        !confirm(
          `${e.entryNumber} is matched to a bank-feed transaction. Deleting it will release that bank transaction back to the unmatched feed.\n\nPermanently delete? This cannot be undone.`,
        )
      )
        return;
    } else if (!confirm(`Permanently delete ${e.entryNumber}? This cannot be undone.`)) {
      return;
    }
    del.mutate(e.id);
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : page * pageSize + 1;
  const lastRow = Math.min(total, page * pageSize + entries.length);

  const toggleHideVoid = (next: boolean) => {
    setHideVoid(next);
    setSearch({ page: 1 });
    window.localStorage.setItem(HIDE_VOID_KEY, next ? 'true' : 'false');
  };
  const setPage = (zeroBased: number) => setSearch({ page: zeroBased + 1 });
  const changePageSize = (next: number) => setSearch({ pageSize: next });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Journal</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 24, margin: 0 }}>Journal entries</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label className="muted" style={{ fontSize: 14 }} htmlFor="j-account">
              Account
            </label>
            <div style={{ minWidth: 260 }}>
              <AccountPicker
                id="j-account"
                accounts={accounts}
                value={accountId}
                onChange={(id) => setSearch({ account: id })}
                // Header accounts can't be posted to, so filtering by one would
                // always return nothing. Inactive accounts stay selectable —
                // history posted to them still needs to be findable.
                predicate={(a) => a.allowDirectPosting}
                placeholder="All accounts"
                title="Show only entries with a line hitting this account"
              />
            </div>
            {accountId && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSearch({ account: '' })}
                title="Show entries for all accounts"
              >
                Clear
              </button>
            )}
          </div>
          <label
            style={{
              display: 'inline-flex',
              gap: 6,
              alignItems: 'center',
              color: 'var(--text-muted)',
              fontSize: 14,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={hideVoid}
              onChange={(e) => toggleHideVoid(e.target.checked)}
              style={{ margin: 0 }}
            />
            Hide voided
            {hideVoid && voidCount === 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                (no voided shown)
              </span>
            )}
            {!hideVoid && voidCount > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                ({voidCount} voided shown)
              </span>
            )}
          </label>
          <Link
            to="/journal/new"
            className="btn-primary"
            style={{ display: 'inline-block', textDecoration: 'none' }}
          >
            New entry
          </Link>
        </div>
      </div>

      {!isLoading && !error && (
        <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
          {total} {total === 1 ? 'entry' : 'entries'}
          {accountLabel ? ` in ${accountLabel}` : ''}
        </p>
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load entries.</p>}

      {!isLoading && entries.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          {accountId ? (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                No journal entries hit {accountLabel ?? 'this account'}.
              </p>
              <button className="btn-secondary" onClick={() => setSearch({ account: '' })}>
                Show all accounts
              </button>
            </>
          ) : (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                No journal entries yet.
              </p>
              <Link
                to="/journal/new"
                className="btn-primary"
                style={{ display: 'inline-block', textDecoration: 'none' }}
              >
                Post your first entry
              </Link>
            </>
          )}
        </div>
      )}

      {entries.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Date</th>
              <th>Memo</th>
              <th>Accounts</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>Source</th>
              <th>Bank</th>
              <th>Status</th>
              <th aria-label="Delete" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 500 }}>
                  <Link to="/journal/$id" params={{ id: e.id }}>
                    {e.entryNumber}
                  </Link>
                </td>
                <td>{formatAccountingDate(e.date)}</td>
                <td>{e.memo ?? '—'}</td>
                <td className="muted" style={{ fontSize: 12 }}>{e.accountSummary ?? ''}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {e.total != null ? formatCurrency(e.total) : ''}
                </td>
                <td>{journalSourceLabel(e.source)}</td>
                <td>
                  {e.bankMatchStatus === 'reconciled' ? (
                    <span className="tag ok" title="Matched to a bank transaction and reconciled">
                      reconciled
                    </span>
                  ) : e.bankMatchStatus === 'matched' ? (
                    <span className="tag" title="Matched to a bank-feed transaction">
                      matched
                    </span>
                  ) : (
                    <span className="muted" title="No bank-feed transaction is linked to this entry">
                      —
                    </span>
                  )}
                </td>
                <td>
                  {e.isVoid ? (
                    <span className="tag bad">void</span>
                  ) : e.isLocked ? (
                    <span
                      className="tag warn"
                      title="At least one line on this entry is frozen by a completed reconciliation. The other lines can still be reconciled against their own statements — open the entry to see which leg is frozen."
                    >
                      reconciled
                    </span>
                  ) : (
                    <span className="tag ok">posted</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {!e.isVoid && (
                    <button
                      type="button"
                      className="icon-link"
                      style={{ width: 28, height: 28, fontSize: 15 }}
                      title={`Delete ${e.entryNumber}`}
                      aria-label={`Delete ${e.entryNumber}`}
                      disabled={del.isPending}
                      onClick={() => onDelete(e)}
                    >
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {total > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 12,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            {firstRow}–{lastRow} of {total}
            {isFetching && ' · loading…'}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label className="muted" style={{ fontSize: 13 }} htmlFor="j-pagesize">
              Per page
            </label>
            <select
              id="j-pagesize"
              value={pageSize}
              onChange={(e) => changePageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              disabled={page === 0}
              onClick={() => setPage(0)}
              title="First page"
            >
              «
            </button>
            <button
              className="btn-secondary"
              disabled={page === 0}
              onClick={() => setPage(Math.max(0, page - 1))}
            >
              ‹ Prev
            </button>
            <span className="muted" style={{ fontSize: 13 }}>
              Page {page + 1} of {pageCount}
            </span>
            <button
              className="btn-secondary"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage(page + 1)}
            >
              Next ›
            </button>
            <button
              className="btn-secondary"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage(pageCount - 1)}
              title="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
